import { Injectable, Inject, Injector } from '@angular/core';
import { Http, Response, Headers, RequestOptions } from '@angular/http';

import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/observable/throw';
import { AuthService } from '../auth/auth.service';
import { Oauth2Service } from '../resources/oauth-2.service';
import { LocalConnectionService } from '../common/local-connection.service';
import { ISmartenitConfig } from '../smartenit-config.interface';
import { IRequestOptions } from './request-options.interface';
import { AppConfigurationService } from '../common/app-configuration.service';
import { EventsManagerService } from '../common/events-manager.service';
import { HttpInterceptor } from '../common/http-interceptor.service';

@Injectable()
export class APIClientService {
  private static readonly ACCESS_TOKEN_ERRORS = [114, 115];

  protected backendDataLimit: number = 25;

  get apiURL(): string {
    return this.AppConfiguration.currentAPIURL;
  }

  get isInLAPIMode() {
    return !!this.AppConfiguration.currentConfig.currentGateway;
  }

  private _onServerError: Subject<any> = new Subject();

  public get onServerError(): Observable<any> {
    return this._onServerError.asObservable();
  }

  constructor(
    protected defaultPath: string,
    protected http: HttpInterceptor,
    public authService: AuthService,
    public eventsManagerService: EventsManagerService,
    public AppConfiguration: AppConfigurationService,
    public injector: Injector
  ) {
    this.defaultPath = defaultPath ? defaultPath : '';
  }

  public parseError(error: any) {
    try {
      let errorData = error;
      if (error._body && typeof error._body == 'string') {
        errorData = JSON.parse(error._body);
      } else if (error._body && typeof error._body == 'object') {
        errorData = error._body;
      }
      return errorData;
    } catch (parseError) {
      console.log('Response Parse error: ' + parseError.toString());
      return error;
    }
  }

  public get(
    path?: string | null,
    data?: any,
    options?: IRequestOptions
  ): Observable<any> {
    const headers = new Headers();
    const innerOptions = new RequestOptions({ headers, withCredentials: true });

    let resourcePath = this.apiURL;

    if (this.defaultPath) {
      resourcePath += '/' + this.defaultPath;
    }

    if (
      !options ||
      options.credentials === undefined ||
      options.credentials === true
    ) {
      if (this.authService.getAccessToken()) {
        headers.append(
          'Authorization',
          'Bearer ' + this.authService.getAccessToken()
        );
      } else if (this.AppConfiguration.currentConfig.apiKey) {
        headers.append('Authorization', `SMARTENIT-API-KEY ${this.AppConfiguration.currentConfig.apiKey}`);
      }
    }

    if (path) {
      resourcePath += '/' + path;
    }

    if (data) {
      resourcePath = this.addUrlParams(data, resourcePath);
    }

    return this.http
      .get(resourcePath, innerOptions)
      .map(this.extractData)
      .catch((error: any) => {
        let errorData;
        errorData = this.parseError(error);

        const errors =
          (errorData && errorData.error && errorData.error.errors) || [];
        const accessToken = this.authService.getAccessToken();

        /**
         * code 114 means Invalid access_token
         */
        if (
          (accessToken &&
            errors.length &&
            APIClientService.ACCESS_TOKEN_ERRORS.some(
              (e) => e === errors[0].code
            )) ||
          (accessToken && this.isInLAPIMode && error.status === 401)
        ) {
          const oauth2Service: Oauth2Service = this.injector.get(Oauth2Service);
          return oauth2Service.refreshToken(false).flatMap((response) => {
            return this.get(path, data, options);
          });
        }

        return this.handleError(error);
      });
  }

  private addUrlParams(data: any, resourcePath: string): string {
    if (data) {
      const params = <any> [];
      const keys = Object.keys(data);
      for (let i = 0; i < keys.length; i++) {
        params.push(keys[i] + '=' + encodeURIComponent(data[keys[i]]));
      }

      if (params.length > 0) {
        resourcePath += '?' + params.join('&');
      }
    }

    return resourcePath;
  }

  public remove(
    path?: string,
    data?: any,
    options?: IRequestOptions
  ): Observable<any> {
    const headers = new Headers();
    const innerOptions = new RequestOptions({ headers, withCredentials: true });

    let resourcePath = this.apiURL;

    if (this.defaultPath) {
      resourcePath += '/' + this.defaultPath;
    }

    if (
      !options ||
      options.credentials === undefined ||
      options.credentials === true
    ) {
      if (this.authService.getAccessToken()) {
        headers.append(
          'Authorization',
          'Bearer ' + this.authService.getAccessToken()
        );
      } else if (this.AppConfiguration.currentConfig.apiKey) {
        headers.append('Authorization', `SMARTENIT-API-KEY ${this.AppConfiguration.currentConfig.apiKey}`);
      }
    }

    if (path) {
      resourcePath += '/' + path;
    }

    if (data) {
      resourcePath = this.addUrlParams(data, resourcePath);
    }

    return this.http
      .delete(resourcePath, innerOptions)
      .map(this.extractData)
      .catch((error: any) => {
        let errorData;
        errorData = this.parseError(error);

        const errors =
          (errorData && errorData.error && errorData.error.errors) || [];

        const accessToken = this.authService.getAccessToken();

        /**
         * code 114 means Invalid access_token
         */
        if (
          (accessToken &&
            errors.length &&
            APIClientService.ACCESS_TOKEN_ERRORS.some(
              (e) => e === errors[0].code
            )) ||
          (accessToken && this.isInLAPIMode && error.status === 401)
        ) {
          const oauth2Service: Oauth2Service = this.injector.get(Oauth2Service);
          return oauth2Service.refreshToken(false).flatMap((response) => {
            return this.remove(path, data, options);
          });
        }

        /**
         * Conflict case
         */
        if (error.status === 409) {
          return Observable.throw(
            Object.assign({}, error, {
              data: errorData
            })
          );
        }

        /**
         * Resource does not exists, then remove it locally
         */
        if (error.status === 404) {
          return Observable.of({ data: { _id: path } });
        }

        return this.handleError(error);
      });
  }

  public post(
    path?: string | null,
    data?: any,
    options?: any
  ): Observable<any> {
    const bodyString = JSON.stringify(data);
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const innerOptions = new RequestOptions({ headers, withCredentials: true });

    let resourcePath = this.apiURL;

    if (this.defaultPath) {
      resourcePath += '/' + this.defaultPath;
    }

    if (path) {
      resourcePath += '/' + path;
    }

    if (
      !options ||
      options.credentials === undefined ||
      options.credentials === true
    ) {
      if (this.authService.getAccessToken()) {
        headers.append(
          'Authorization',
          'Bearer ' + this.authService.getAccessToken()
        );
      } else if (this.AppConfiguration.currentConfig.apiKey) {
        headers.append('Authorization', `SMARTENIT-API-KEY ${this.AppConfiguration.currentConfig.apiKey}`);
      }
    }

    if (options && options.data) {
      resourcePath = this.addUrlParams(options.data, resourcePath);
    }

    return this.http
      .post(resourcePath, bodyString, innerOptions)
      .map(this.extractData)
      .catch((error: any) => {
        let errorData;
        errorData = this.parseError(error);

        const errors =
          (errorData && errorData.error && errorData.error.errors) || [];
        const accessToken = this.authService.getAccessToken();

        /**
         * code 114 means Invalid access_token
         */
        if (
          (accessToken &&
            errors.length &&
            APIClientService.ACCESS_TOKEN_ERRORS.some(
              (e) => e === errors[0].code
            )) ||
          (accessToken && this.isInLAPIMode && error.status === 401)
        ) {
          const oauth2Service: Oauth2Service = this.injector.get(Oauth2Service);
          return oauth2Service.refreshToken(false).flatMap((response) => {
            return this.post(path, data, options);
          });
        }

        return this.handleError(error);
      });
  }

  public put(path?: string, data?: any, options?: any): Observable<any> {
    const bodyString = JSON.stringify(data);
    const headers = new Headers({ 'Content-Type': 'application/json' });
    const innerOptions = new RequestOptions({ headers, withCredentials: true });

    let resourcePath = this.apiURL;

    if (this.defaultPath) {
      resourcePath += '/' + this.defaultPath;
    }

    if (path) {
      resourcePath += '/' + path;
    }

    if (
      !options ||
      options.credentials === undefined ||
      options.credentials === true
    ) {
      if (this.authService.getAccessToken()) {
        headers.append(
          'Authorization',
          'Bearer ' + this.authService.getAccessToken()
        );
      } else if (this.AppConfiguration.currentConfig.apiKey) {
        headers.append('Authorization', `SMARTENIT-API-KEY ${this.AppConfiguration.currentConfig.apiKey}`);
      }
    }

    if (options && options.data) {
      resourcePath = this.addUrlParams(options.data, resourcePath);
    }

    return this.http
      .put(resourcePath, bodyString, innerOptions)
      .map(this.extractData)
      .catch((error: any) => {
        let errorData;
        errorData = this.parseError(error);

        const errors =
          (errorData && errorData.error && errorData.error.errors) || [];
        const accessToken = this.authService.getAccessToken();

        /**
         * code 114 means Invalid access_token
         */
        if (
          (accessToken &&
            errors.length &&
            APIClientService.ACCESS_TOKEN_ERRORS.some(
              (e) => e === errors[0].code
            )) ||
          (accessToken && this.isInLAPIMode && error.status === 401)
        ) {
          const oauth2Service: Oauth2Service = this.injector.get(Oauth2Service);
          return oauth2Service.refreshToken(false).flatMap((response) => {
            return this.put(path, data, options);
          });
        }

        /**
         * Conflict case
         */
        if (error.status === 409) {
          return Observable.throw(
            Object.assign({}, error, {
              data: errorData
            })
          );
        }

        return this.handleError(error);
      });
  }

  private extractData(res: Response) {
    return res.json();
  }

  private handleError(error: Response | any) {
    let errObj: any;
    if (error instanceof Response) {
      let body;
      try {
        body = error.json();
        if (body.error) {
          errObj = body.error;
        } else {
          errObj = body;
        }
      } catch (e) {
        console.log('Error:', e);
      }
    } else {
      errObj = error.message
        ? { message: error.message }
        : { message: error.toString() };
    }

    return Observable.throw(errObj);
  }
}
